import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { AllowedLanguages } from 'i18n';
import * as Yup from 'yup';

import { addHostToUrl, downloadFile } from 'services/http/http';
import { useCreatePartner, useCurrentPartner } from 'services/repositories/partners/partners';
import { useCreateTradeRequest } from 'services/repositories/relationships/relationships';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { IdentifierDomainOptions } from 'support/types';
import { Alert } from 'components/Display/Alert/Alert';
import { AlertSection } from 'components/Display/Alert/AlertSection';
import { SlideOver } from 'components/Display/SlideOver/SlideOver';
import { Button } from 'components/Form/Button/Button';
import { CheckboxInABox } from 'components/Form/Checkbox/Checkbox';
import { FileInput } from 'components/Form/FileInput/FileInput';
import { csvLoader } from 'components/Form/FileInput/Loaders/csvLoader/csvLoader';
import { Form } from 'components/Form/Form';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';

import { useOwnGlns } from '../hooks/useOwnGlns';
import { DocumentSettingsCheckboxes } from '../DocumentSettingsCheckboxes/DocumentSettingsCheckboxes';
import {
  createTradePartnerRequests,
  defaultDocumentSettings,
  DocumentTypeSetting,
  mapPartnerDocumentTypes,
} from '../helpers';
import { CSVErrorDetailsModal } from './components/CSVErrorDetailsModal';
import { collectCSVValidationErrors, createValidateCSV, CSVRow, CSVRowKeys, CSVValidationErrorDetail } from './helpers';

type NewBulkTradePartnerProps = { showPopup: boolean; setShowPopup: (value: boolean) => void };

type NewTradePartnerForm = {
  tradePartners: Array<{
    name: string;
    contactPersons: Array<{ name: string; email: string; phone: string; language: AllowedLanguages }>;
    partyIdentifierDomain?: IdentifierDomainOptions;
    partyIdentifier?: string;
  }>;
  documentTypeSettings: Array<any>; //having issues with required and nullable fiels on schemas so using any for now. ChaGPT + co-pilot didn't help
  includeAsCCRecipient?: boolean;
};

const documentTypeSettingSchema = Yup.object();

const contactPersonSchema = Yup.object()
  .shape({
    name: Yup.string().required(),
    email: Yup.string().required(),
    phone: Yup.string().required(),
    language: Yup.string().oneOf(['en', 'de']).required(),
  })
  .required();

export function NewBulkTradePartner({ showPopup, setShowPopup }: NewBulkTradePartnerProps) {
  const { t } = useTranslation();
  const { addNotification } = useNotificationStore();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);

  const { data: currentPartner } = useCurrentPartner();
  const preferredIdentifierDomain = currentPartner?.preferredIdentifierDomain;
  const hasBothDomains =
    !!preferredIdentifierDomain?.includes('internal') && !!preferredIdentifierDomain?.includes('GS1');
  const isInternalIdentifier = preferredIdentifierDomain?.includes('internal') ?? false;
  const ownGlns = useOwnGlns({ enabled: showPopup });

  const {
    handleSubmit,
    setValue,
    register,
    reset,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm<NewTradePartnerForm>({
    defaultValues: { tradePartners: [], documentTypeSettings: defaultDocumentSettings, includeAsCCRecipient: true },
    mode: 'all',
    resolver: yupResolver(createNewTradePartnerFormSchema(true)),
  });

  const documentTypeSettings = useWatch({ name: 'documentTypeSettings', control });
  const createPartnerMutation = useCreatePartner();
  const createTradeRequestMutation = useCreateTradeRequest();

  const isLoadingSubmission = createPartnerMutation.isLoading || createTradeRequestMutation.isLoading;

  const [csvErrorDetails, setCsvErrorDetails] = useState<Array<CSVValidationErrorDetail>>([]);
  const [showErrorDetailsModal, setShowErrorDetailsModal] = useState(false);

  const validateCSV = useMemo(
    () => createValidateCSV({ identifierDomain: preferredIdentifierDomain, ownGlns }),
    [preferredIdentifierDomain, ownGlns],
  );

  const templateFile = addHostToUrl(
    hasBothDomains
      ? '/assets/TradePartnersTemplate_ID_GLN.csv'
      : isInternalIdentifier
        ? '/assets/TradePartnersTemplate_ID.csv'
        : '/assets/TradePartnersTemplate_GLN.csv',
  );

  useEffect(() => {
    if (currentPartner) {
      setValue(
        'documentTypeSettings',
        mapPartnerDocumentTypes({ partnerDocuments: currentPartner.documentTypes, isCurrentPartner: true }),
        { shouldValidate: true },
      );
    }
  }, [currentPartner, setValue]);

  const PrimaryButton = () => (
    <Button
      type="submit"
      form="newBulkTradePartnerForm"
      loading={isLoadingSubmission}
      disabled={!isValid || isSubmitting || !!Object.keys(errors).length}
      analyticsId="partners:bulk_trade_partner_submit"
    >
      <div data-testid="action-button">{t('partners:request.newBulkTradePartner.primaryButton')}</div>
    </Button>
  );

  const CancelButton = () => (
    <Button
      onClick={onClose}
      variant="secondary"
      disabled={isLoadingSubmission}
      analyticsId="partners:bulk_trade_partner_cancel"
    >
      <div data-testid="cancel-button">{t('common:cancel')}</div>
    </Button>
  );

  const onSubmit = async (submission: NewTradePartnerForm) => {
    if (!currentPartner) {
      return;
    }

    setNonInputErrors([]);
    const requestedDocumentTypes = submission.documentTypeSettings.filter(
      (setting) => setting.outgoing.selected || setting.incoming.selected,
    );
    if (!requestedDocumentTypes.length) {
      setNonInputErrors([t('partners:request.noDocumentTypesSelected')]);
      return;
    }

    try {
      await createTradePartnerRequests({
        createPartnerMutation: createPartnerMutation,
        createTradeRequestMutation: createTradeRequestMutation,
        currentPartner,
        submission: {
          requestedDocumentTypes,
          tradePartners: submission.tradePartners,
          includeAsCCRecipient: submission.includeAsCCRecipient,
        },
      });

      onClose();
      addNotification(t('partners:request.newTradePartner.success'));
    } catch (err) {
      processSubmissionErrors({
        error: err as Error,
        setInputError: undefined,
        setNonInputErrors,
        defaultData: undefined,
        t,
      });
    }
  };

  const onClose = () => {
    setShowPopup(false);
    setNonInputErrors([]);
    setCsvErrorDetails([]);
    setShowErrorDetailsModal(false);
    reset();
  };

  const onError = (_error: Error, file: File) => {
    const loader = csvLoader(file);
    loader
      .then((result) => {
        const errorDetails = collectCSVValidationErrors(result.data, {
          identifierDomain: preferredIdentifierDomain,
          ownGlns,
        });
        if (errorDetails.length > 0) {
          setCsvErrorDetails(errorDetails);
        }
      })
      .catch(() => {
        setCsvErrorDetails([]);
      });
  };

  const onClearFileInput = () => {
    setValue('tradePartners', [], { shouldValidate: true });
  };

  const onLoadFile = (data: Array<CSVRow>) => {
    addNotification(t('partners:request.newBulkTradePartner.form.upload.valid'), NotificationType.success);
    setValue(
      'tradePartners',
      data.map((row: CSVRow) => {
        const language = row[CSVRowKeys.Language].toLocaleLowerCase().trim() as AllowedLanguages;
        const rowHasInternal = hasBothDomains ? !!row[CSVRowKeys.InternalIdentifier] : isInternalIdentifier;
        return {
          name: row[CSVRowKeys.TradePartnerName],
          partyIdentifierDomain: rowHasInternal
            ? IdentifierDomainOptions.RECEIVER_INTERNAL
            : IdentifierDomainOptions.GS1,
          partyIdentifier: (rowHasInternal ? row[CSVRowKeys.InternalIdentifier] : row[CSVRowKeys.GLN])?.trim() || '',
          contactPersons: [
            {
              name: row[CSVRowKeys.ContactPerson],
              email: row[CSVRowKeys.EmailAddress],
              phone: row[CSVRowKeys.Telephone],
              language: getContactPersonLanguage(language),
            },
          ],
        };
      }),
      { shouldValidate: true },
    );
  };

  const onDocumentSettingsChange = (documentTypeSettings: Array<DocumentTypeSetting>) => {
    setValue('documentTypeSettings', documentTypeSettings, { shouldValidate: true });
  };

  const downloadTemplate = () => {
    downloadFile(templateFile);
  };

  return (
    <SlideOver
      size="large"
      footer={
        <>
          <CancelButton />
          <PrimaryButton />{' '}
        </>
      }
      title={t('partners:request.newBulkTradePartner.heading')}
      subtitle={t('partners:request.newBulkTradePartner.subHeading')}
      open={showPopup}
      setOpen={onClose}
    >
      <Form id="newBulkTradePartnerForm" onSubmit={handleSubmit(onSubmit)} className="grid gap-6">
        <ValidationErrors errors={nonInputErrors} />

        <Button
          LeftIcon={ArrowDownTrayIcon}
          onClick={downloadTemplate}
          analyticsId="partners:bulk_trade_partner_template_download"
        >
          {t('partners:request.newBulkTradePartner.dowloadTemplate')}
        </Button>
        <FileInput
          label={t('partners:request.newBulkTradePartner.form.upload.label')}
          accept={{ 'text/csv': ['.csv'] }}
          validate={validateCSV}
          onClear={onClearFileInput}
          onError={onError}
          onLoad={onLoadFile}
          loader={csvLoader}
        />

        {csvErrorDetails.length > 0 && (
          <>
            <Alert severity="error" withoutIcon>
              <AlertSection
                severity="error"
                title={t('partners:request.newBulkTradePartner.form.upload.errorDetails.alertTitle')}
              >
                <p className="text-sm text-gray-700">
                  {t('partners:request.newBulkTradePartner.form.upload.errorDetails.alertDescription', {
                    count: csvErrorDetails.length,
                  })}
                </p>
                <div className="mt-4">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setShowErrorDetailsModal(true)}
                    analyticsId="partners:bulk_trade_partner_show_user_error_details"
                  >
                    {t('partners:request.newBulkTradePartner.form.upload.errorDetails.showDetails')}
                  </Button>
                </div>
              </AlertSection>
            </Alert>

            <CSVErrorDetailsModal
              open={showErrorDetailsModal}
              onClose={() => setShowErrorDetailsModal(false)}
              errorDetails={csvErrorDetails}
            />
          </>
        )}

        <DocumentSettingsCheckboxes
          documentTypeSettings={documentTypeSettings}
          setDocumentTypeSettings={onDocumentSettingsChange}
        />

        <CheckboxInABox
          label={t('partners:request.includeAsCCRecipient.label')}
          description={t('partners:request.includeAsCCRecipient.description')}
          {...register('includeAsCCRecipient')}
        />
      </Form>
    </SlideOver>
  );
}

const createTradePartnerSchema = (requiresIdentifier: boolean) => {
  const base = Yup.object().shape({
    name: Yup.string().required(),
    contactPersons: Yup.array().of(contactPersonSchema).required().min(1),
  });

  if (requiresIdentifier) {
    return base.shape({
      partyIdentifierDomain: Yup.mixed<IdentifierDomainOptions>()
        .oneOf(Object.values(IdentifierDomainOptions))
        .required(),
      partyIdentifier: Yup.string().required(),
    });
  }

  return base;
};

const createNewTradePartnerFormSchema = (isInternalIdentifier: boolean) =>
  Yup.object().shape({
    tradePartners: Yup.array().of(createTradePartnerSchema(isInternalIdentifier)).required().min(1),
    documentTypeSettings: Yup.array()
      .of(documentTypeSettingSchema)
      .required()
      .min(1)
      .test('', function (array) {
        return array.some((item) => (item as DocumentTypeSetting).incoming || (item as DocumentTypeSetting).outgoing);
      }),
    includeAsCCRecipient: Yup.boolean(),
  });

const getContactPersonLanguage = (language: string): AllowedLanguages => {
  const possibleSupportedLanguageCode = language?.substring(0, 2).toLowerCase();
  return ['de', 'en'].includes(possibleSupportedLanguageCode)
    ? (possibleSupportedLanguageCode as AllowedLanguages)
    : 'de';
};
